import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { createSelector } from 'reselect';

import { StoreState } from '../../../reducers';
import { getNumberFromString, getRoundedValue, getValueAndUnits } from '../utils/helpers';
import { Color } from '../../../components/Editor/EditPanel/Select/styles';
import { TextFieldsEditableData } from '../utils/editableTextFields/textFieldsEditableData';
import { EditableDataState } from '../reducer';

interface BasicOption {
	values: string[];
	label: string;
}

interface FontFamilyOption {
	values: string[];
	label: React.ReactElement;
}

interface ColorOption {
	values: string[];
	label: React.ReactElement;
}

const getEditableData = (state: StoreState): EditableDataState => state.editor.configs.generalData.editablePublication;

const getPageTextFieldsData = createSelector(
	getEditableData,
	(state: StoreState, page: number): number => page,
	(editableData: EditableDataState, page: number): TextFieldsEditableData | undefined => editableData[page] && editableData[page].textFieldsData,
);

// tslint:disable-next-line: max-line-length
// some options (font-size, line-height, letter-spacing) can have very close values set in inDesign, e.g., '12px' and '12.2px'. Both of these values should be kept for defining if the value is selected in edit panel but only one rounded value ('12px') should be displayed
const getOptions = (data: Set<string> | string[], decimal: number = 1): BasicOption[] => {
	const dataObject = [...data].reduce((acc, current) => {
		const { value, units } = getValueAndUnits(current);
		const label = `${getRoundedValue(value, decimal)}${units}`;

		if (!acc[label]) {
			acc[label] = {
				label,
				values: [current],
			};
		} else {
			acc[label].values.push(current);
		}

		return acc;
	}, {});

	return Object.keys(dataObject)
		.map(key => ({
			label: key,
			values: dataObject[key].values,
		}))
		.sort((a, b) => getNumberFromString(a.label) - getNumberFromString(b.label));
};

export const getFontFamilyOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): FontFamilyOption[] => {
		if (!data) {
			return [];
		}

		return [...data.fontFamilies].map(fontFamily => ({
			values: [fontFamily],
			label: (
				<span style={{ fontFamily }}>
					{fontFamily.split(',')[0].replace(/\'|\"/g, '')}
				</span>
			),
		}));
	}
);

export const getFontSizeOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): BasicOption[] => {
		if (!data) {
			return [];
		}

		return getOptions(data.fontSizes);
	}
);

export const getColorOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): ColorOption[] => {
		if (!data) {
			return [];
		}

		return [...data.colors].map(color => ({
			values: [color],
			label: <Color style={{ background: color }} />,
		}));
	}
);

export const getLineHeightOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): BasicOption[] => {
		if (!data) {
			return [];
		}

		const options = getOptions(data.lineHeights);

		if ([...data.lineHeights].includes('normal')) {
			options.push({
				label: I18n.t('messages.normal'),
				values: ['normal'],
			});
		}

		return options;
	}
);

export const getLetterSpacingOptions = createSelector(
	getPageTextFieldsData,
	(data: TextFieldsEditableData | undefined): BasicOption[] => {
		if (!data) {
			return [];
		}

		return getOptions(['0px', ...data.letterSpacings], 10);
	}
);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/selectors/textFieldsData.tsx